/**
 * Shared locale-aware relative time formatting.
 *
 * All call sites pass a `t` function from `useTranslations('time')` so the
 * output language matches the user's selected locale. Two input formats are
 * supported: ISO timestamp strings and Unix-second timestamps.
 *
 * Style variants:
 *   - `'long'`  (default) → "5m ago" / "5分前"  (keys: `minutesAgo`, `hoursAgo`, …)
 *   - `'short'`           → "5m" / "5分"        (keys: `minutesShort`, `hoursShort`, …)
 *   - `'age'`             → "5 minutes" / "New"  (keys: `minutesOld`, `hoursOld`, …;
 *                            the market-card footer pairs these with a leaf icon)
 *
 * The `includeSeconds` option adds a seconds-precision tier before the minutes
 * tier (used by the proposal backers list).
 */

/**
 * Translation function shape. Uses a simple callback to avoid coupling to
 * next-intl's branded key types. Call sites create a thin wrapper:
 * `(key, params) => tTime(key, params)` which satisfies both TypeScript and next-intl.
 */
type TranslationFn = (key: string, params?: Record<string, number>) => string;

interface FormatOptions {
  /** `'long'` (default) "5m ago"; `'short'` "5m"; `'age'` "5mins old". */
  style?: 'long' | 'short' | 'age';
  /** When true, adds a seconds tier for timestamps < 60s old. Default: false. */
  includeSeconds?: boolean;
  /** BCP 47 locale tag passed to `Intl.DateTimeFormat` for the >12-month fallback. */
  locale?: string;
  /** Explicit clock snapshot for deterministic multi-row formatting. */
  nowMs?: number;
}

const MS_SEC: number = 1_000;
const MS_MIN: number = 60 * MS_SEC;
const MS_HR: number = 60 * MS_MIN;
const MS_DAY: number = 24 * MS_HR;
const DAYS_IN_MONTH: number = 30;
const MAX_MONTHS: number = 12;

/**
 * Format an ISO timestamp string into a compact locale-aware relative time.
 *
 * Returns `''` for empty or invalid timestamps.
 */
export function formatRelativeTime(
  timestamp: string,
  t: TranslationFn,
  options?: FormatOptions,
): string {
  if (timestamp === '') return '';
  const then = new Date(timestamp).getTime();
  if (Number.isNaN(then)) return '';
  const nowMs = options?.nowMs ?? Date.now();
  return formatDiffMs(nowMs - then, t, options, nowMs);
}

/**
 * Format a Unix-second timestamp into a compact locale-aware relative time.
 */
export function formatRelativeTimestamp(
  timestampSec: number,
  t: TranslationFn,
  options?: FormatOptions,
): string {
  const nowMs = options?.nowMs ?? Date.now();
  return formatDiffMs(nowMs - timestampSec * MS_SEC, t, options, nowMs);
}

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

function formatDiffMs(
  diffMs: number,
  t: TranslationFn,
  options: FormatOptions | undefined,
  nowMs: number,
): string {
  const style = options?.style ?? 'long';
  const includeSeconds = options?.includeSeconds === true;

  // Future or essentially now
  if (diffMs < 0) diffMs = 0;

  const diffSec = Math.floor(diffMs / MS_SEC);
  const diffMin = Math.floor(diffMs / MS_MIN);
  const diffHr = Math.floor(diffMs / MS_HR);
  const diffDay = Math.floor(diffMs / MS_DAY);
  const diffMonth = Math.floor(diffDay / DAYS_IN_MONTH);

  if (style === 'short') {
    if (diffMin < 1) return t('lessThanMinuteShort');
    if (diffMin < 60) return t('minutesShort', { count: diffMin });
    if (diffHr < 24) return t('hoursShort', { count: diffHr });
    if (diffDay < 14) return t('daysShort', { count: diffDay });
    if (diffDay < DAYS_IN_MONTH * 2) return t('weeksShort', { count: Math.floor(diffDay / 7) });
    if (diffMonth < MAX_MONTHS) return t('monthsShort', { count: diffMonth });
    return new Intl.DateTimeFormat(options?.locale, { dateStyle: 'medium' }).format(
      new Date(nowMs - diffMs),
    );
  }

  // Age style — spelled elapsed-since-creation ("2 hours", "2 days"), shown next
  // to a leaf icon that carries the "age" meaning visually. Same tier boundaries
  // as `short`; the sub-minute tier reads "New", and the oldest tier keeps
  // counting months (an age never falls back to an absolute date).
  if (style === 'age') {
    if (diffMin < 1) return t('lessThanMinuteOld');
    if (diffMin < 60) return t('minutesOld', { count: diffMin });
    if (diffHr < 24) return t('hoursOld', { count: diffHr });
    if (diffDay < 14) return t('daysOld', { count: diffDay });
    if (diffDay < DAYS_IN_MONTH * 2) return t('weeksOld', { count: Math.floor(diffDay / 7) });
    return t('monthsOld', { count: diffMonth });
  }

  // Long style
  if (diffMin < 1) {
    if (includeSeconds && diffSec > 0) return t('secondsAgo', { count: diffSec });
    return t('justNow');
  }
  if (diffMin < 60) return t('minutesAgo', { count: diffMin });
  if (diffHr < 24) return t('hoursAgo', { count: diffHr });
  if (diffDay < DAYS_IN_MONTH) return t('daysAgo', { count: diffDay });
  if (diffMonth < MAX_MONTHS) return t('monthsAgo', { count: diffMonth });

  // Older than 12 months — locale-formatted absolute date
  return new Intl.DateTimeFormat(options?.locale, { dateStyle: 'medium' }).format(
    new Date(nowMs - diffMs),
  );
}
